import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, Flex, Heading, Text, TextWithIcon, Tooltip } from '@modusign/design-system';
import { useListAutomaticSharings } from '@modusign/openapi-client-user';
import { ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import Chip from '@app/components/Chip';
import {
  useCurrentMemberGroupList,
  useCurrentUserMemberId,
  useCurrentUserRole,
  useWorkspace,
} from '@app/hooks/workspace';

function MemberInfoCardContent() {
  const ws = useWorkspace();
  const memberId = useCurrentUserMemberId();
  const role = useCurrentUserRole();
  const groups = useCurrentMemberGroupList();
  const { data: automaticSharings } = useListAutomaticSharings(
    ws.id,
    { memberIds: [memberId] },
    {
      query: {
        suspense: true,
        select: (data) => {
          return data.automaticSharings.find((as) => as.memberId === memberId)?.targets || [];
        },
      },
    }
  );

  return (
    <Flex flexDirection="column" gap={7}>
      <InputSet label="역할">
        <Text>{role?.name ?? '없음'}</Text>
      </InputSet>
      <InputSet label="그룹">
        <Flex flexWrap="wrap" sx={{ gap: 3 }}>
          {groups.map((g) => (
            <Chip key={g.id} sx={{ ml: 0 }}>
              {g.name}
            </Chip>
          ))}
        </Flex>
      </InputSet>
      <InputSet
        label={
          <TextWithIcon
            iconContainerWidth="14px"
            rightIcon={
              <Tooltip
                content={
                  <Text>
                    내가 생성하는 문서가 모두
                    <br />
                    공유되는 멤버·그룹입니다.
                  </Text>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Tooltip>
            }
          >
            <Text fontSize="sm" fontWeight="medium">
              문서 자동 공유
            </Text>
          </TextWithIcon>
        }
      >
        <Flex flexWrap="wrap" sx={{ gap: 3 }}>
          {automaticSharings?.length
            ? automaticSharings.map((v) => (
                <Chip key={v.id} sx={{ ml: 0 }}>
                  {v.member?.name || v.group?.name || ''}
                </Chip>
              ))
            : '없음'}
        </Flex>
      </InputSet>
    </Flex>
  );
}

export default function MemberInfoCard() {
  const ws = useWorkspace();

  if (ws.type === 'PRIVATE') {
    return null;
  }

  return (
    <Card display="flex" flexDirection={['column', null, 'row']} position="relative">
      <Heading
        as="h2"
        flex="none"
        fontSize="18px"
        fontWeight={700}
        sx={{ width: '120px', mb: 6, mr: 6 }}
      >
        멤버
      </Heading>
      <ErrorBoundary fallback={<>멤버 정보 불러오기 실패</>}>
        <Suspense fallback={<CenterLoadingSpinner />}>
          <MemberInfoCardContent />
        </Suspense>
      </ErrorBoundary>
    </Card>
  );
}

function InputSet({ children, label }: { children: ReactNode; label: ReactNode }) {
  return (
    <Flex alignItems={['stretch', null, 'start']} flexDirection={['column', null, 'row']}>
      <Text
        as="label"
        color="textNeutralLight"
        fontSize="sm"
        fontWeight="medium"
        sx={{ flex: 'none', mb: [3, null, 0], width: '140px', mr: 4 }}
      >
        {label}
      </Text>
      <Box flex="auto">{children}</Box>
    </Flex>
  );
}
